'use client';
import { useState, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { FiMenu } from 'react-icons/fi';
import Link from 'next/link';
import DashboardSidebar from './components/DashboardSidebar';
import ProfileSection from './components/ProfileSection';
import MatchesSection from './components/MatchesSection';
import PaymentsHistory from './components/PaymentsHistory';
import AuthModal from '@/components/auth/AuthModal';

// Dashboard shell shown to logged-out visitors (e.g. those landing here from a
// WhatsApp match link) behind the login gate. Reuses the REAL DashboardSidebar +
// the same mobile header as the logged-in view, so the chrome is identical to what
// users normally see (no bespoke styling). Only the data area is a neutral blurred
// skeleton. The whole thing sits behind the modal backdrop, so it's non-interactive.
function LockedDashboardPreview() {
  const guest = { id: '', name: 'Guest', email: '' };
  return (
    // pointer-events-none: nothing behind the login gate is clickable — any click
    // anywhere on the page lands on the (non-dismissible) modal, so login comes first.
    <div className="min-h-screen flex bg-slate-50 w-full pointer-events-none select-none">
      {/* Mobile Header — same markup as the logged-in dashboard */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <button className="p-2 rounded-lg">
              <FiMenu className="w-6 h-6 text-slate-700" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900">My Matches</h1>
          </div>
          <div className="w-8 h-8 bg-zinc-900 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
        </div>
      </div>

      {/* Real sidebar with a guest placeholder (preview mode: no logout/collapse) */}
      <DashboardSidebar user={guest} onLogout={() => {}} selectedMenu="matches" onMenuSelect={() => {}} preview />

      {/* Data area — neutral blurred skeleton */}
      <main className="flex-1 p-6 overflow-y-auto lg:pt-6 pt-20 h-screen">
        <div aria-hidden className="pointer-events-none select-none blur-[3px] opacity-60">
          <div className="flex gap-2 mb-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-8 w-28 rounded-full bg-slate-200" />
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-2 max-w-4xl">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200" />
                  <div className="flex-1">
                    <div className="h-4 w-32 bg-slate-200 rounded mb-2" />
                    <div className="h-3 w-24 bg-slate-100 rounded" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 w-full bg-slate-100 rounded" />
                  <div className="h-3 w-3/4 bg-slate-100 rounded" />
                </div>
                <div className="h-9 w-full bg-slate-100 rounded-lg mt-4" />
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

function DashboardContent() {
  const { user, loading, logout } = useAuth();
  const searchParams = useSearchParams();
  const router = useRouter();
  // Default to Matches — users landing on bare /dashboard (avatar menu, magic
  // links, thank-you page) want their matches, not the profile form.
  const [selectedMenu, setSelectedMenu] = useState('matches');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Select a menu/tab AND reflect it in the address bar (?section=&tab=)
  const applyMenu = (key: string) => {
    setSelectedMenu(key);
    const [sec, tb] = key.split(':');
    const qs = tb ? `?section=${sec}&tab=${tb}` : `?section=${sec}`;
    router.replace(`/dashboard${qs}`, { scroll: false });
  };

  // Handle URL parameters to set initial menu (supports ?section=matches&tab=magic).
  // Render-phase sync from the changing searchParams (runs on mount + whenever it changes),
  // replacing an effect that only called setState.
  const [prevParams, setPrevParams] = useState<typeof searchParams | null>(null);
  if (searchParams !== prevParams) {
    setPrevParams(searchParams);
    const section = searchParams.get('section');
    const tab = searchParams.get('tab');
    if (section && ['profile', 'matches', 'payments'].includes(section)) {
      setSelectedMenu(section === 'matches' && tab ? `matches:${tab}` : section);
    }
  }

  // selectedMenu may be 'matches' or 'matches:<tab>'
  const section = selectedMenu.split(':')[0];
  const matchesTab = selectedMenu.includes(':') ? selectedMenu.split(':')[1] : undefined;

  const handleLogout = async () => {
    await logout();
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Auth resolves only on the client. Render a deterministic loading screen for
  // SSR + the first client render so they match (no hydration mismatch), and so
  // the AuthModal — which only exists client-side (it portals to document.body) —
  // mounts only AFTER auth is known, never during hydration.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-lg">Loading dashboard…</div>
      </div>
    );
  }

  if (!user) {
    // Dashboard shell (clear chrome, blurred data) with a hard login gate on top.
    // Non-dismissible (no close / no backdrop-click) and a no-blur backdrop so the
    // sidebar + header stay crisp behind it. A successful login sets `user`, so
    // this branch unmounts and the real dashboard renders in place.
    return (
      <>
        <LockedDashboardPreview />
        <AuthModal
          isOpen
          mode="login"
          dismissible={false}
          backdropClassName="bg-slate-900/10"
          onClose={() => {}}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Mobile Header */}
             <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-slate-200 shadow-sm">
         <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <FiMenu className="w-6 h-6 text-slate-700" />
            </button>
            <div>
                           <h1 className="text-lg font-semibold text-slate-900">
                {section === 'profile' ? 'Profile' :
                 section === 'matches' ? 'My Matches' :
                 section === 'payments' ? 'Payments' : 'Dashboard'}
              </h1>
            </div>
          </div>
                     <Link
             href="/"
             className="w-8 h-8 bg-zinc-900 rounded-full flex items-center justify-center hover:bg-zinc-800 transition-colors"
           >
             <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
             </svg>
           </Link>
        </div>
      </div>

      <DashboardSidebar
        user={user}
        onLogout={handleLogout}
        selectedMenu={selectedMenu}
        onMenuSelect={applyMenu}
        isOpen={sidebarOpen}
        onClose={closeSidebar}
      />
      
             {/* Main Content */}
       <main className="flex-1 p-6 overflow-y-auto lg:pt-6 pt-20 h-screen">
         {section === 'profile' && <ProfileSection user={user} />}
         {section === 'matches' && <MatchesSection user={user} tab={matchesTab} onTabChange={(t) => applyMenu(`matches:${t}`)} />}
         {section === 'payments' && <PaymentsHistory user={user} />}
        </main>
    </div>
  );
}

export default function DashboardPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="text-slate-800 text-xl mb-4">Loading dashboard...</div>
        </div>
      </div>
    }>
      <DashboardContent />
    </Suspense>
  );
}
